import { useEffect, useState } from 'react'
import Card from '@components/ui/Card'
import UserList from './UserList'
import ProfileList from './ProfileList'
import Modal from '@components/ui/Modal'
import UserForm from './UserForm'
import ProfileForm from './ProfileForm'
import { listUsers, listProfiles, createUser, updateUser, deleteUser, createProfile, updateProfile, deleteProfile, resetUserPassword } from '@services/userService'
import Grid from '@mui/material/Grid'
import Chip from '@mui/material/Chip'
import Avatar from '@mui/material/Avatar'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Skeleton from '@mui/material/Skeleton'
import Button from '@mui/material/Button'
import PersonIcon from '@mui/icons-material/Person'
import BadgeIcon from '@mui/icons-material/Badge'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import LockResetIcon from '@mui/icons-material/LockReset'
import IconButton from '@mui/material/IconButton'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Tooltip from '@mui/material/Tooltip'
import TextField from '@mui/material/TextField'
import SearchIcon from '@mui/icons-material/Search'
import InputAdornment from '@mui/material/InputAdornment'
import Pagination from '@mui/material/Pagination'

export default function UserManagement({ section = 'users' }) {
  const [users, setUsers] = useState([])
  const [profiles, setProfiles] = useState([])
  const [usersLoading, setUsersLoading] = useState(true)
  const [profilesLoading, setProfilesLoading] = useState(true)
  const [usersError, setUsersError] = useState('')
  const [profilesError, setProfilesError] = useState('')
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false)
  const [selectedProfile, setSelectedProfile] = useState(null)
  const [userSearch, setUserSearch] = useState('')
  const [profileSearch, setProfileSearch] = useState('')
  const [userPage, setUserPage] = useState(1)
  const [profilePage, setProfilePage] = useState(1)
  const itemsPerPage = 6
  const [resetPasswordDialog, setResetPasswordDialog] = useState({ open: false, user: null, tempPassword: null })

  useEffect(() => {
    const abort = new AbortController()
    let active = true
    async function loadUsers() {
      try {
        setUsersLoading(true)
        setUsersError('')
        const u = await listUsers({}, abort.signal)
        if (!active) return
        setUsers(u)
      } catch (e) {
        if (!active) return
        setUsersError(e?.message || 'Falha ao carregar usuários')
      } finally {
        if (active) setUsersLoading(false)
      }
    }
    async function loadProfiles() {
      try {
        setProfilesLoading(true)
        setProfilesError('')
        const p = await listProfiles({}, abort.signal)
        if (!active) return
        setProfiles(p)
      } catch (e) {
        if (!active) return
        setProfilesError('Falha ao carregar perfis')
      } finally {
        if (active) setProfilesLoading(false)
      }
    }
    loadUsers()
    loadProfiles()
    return () => { active = false; abort.abort() }
  }, [])

  const refreshUsers = async () => {
    const abort = new AbortController()
    try {
      setUsersLoading(true)
      const u = await listUsers({}, abort.signal)
      setUsers(u)
    } catch (e) {
      setUsersError('Falha ao carregar usuários')
    } finally {
      setUsersLoading(false)
      abort.abort()
    }
  }

  const refreshProfiles = async () => {
    const abort = new AbortController()
    try {
      setProfilesLoading(true)
      const p = await listProfiles({}, abort.signal)
      setProfiles(p)
    } catch (e) {
      setProfilesError('Falha ao carregar perfis')
    } finally {
      setProfilesLoading(false)
      abort.abort()
    }
  }

  const openUserEdit = (u) => {
    setSelectedUser(u || null)
    setIsUserModalOpen(true)
  }

  const closeUserModal = () => {
    setSelectedUser(null)
    setIsUserModalOpen(false)
  }

  const saveUser = async (data) => {
    try {
      if (selectedUser && selectedUser.id) {
        await updateUser(selectedUser.id, data)
      } else {
        await createUser(data)
      }
      await refreshUsers()
      closeUserModal()
    } catch (e) {
      const errorMsg = e?.response?.data?.password?.[0] ||
                       e?.response?.data?.email?.[0] ||
                       e?.response?.data?.username?.[0] ||
                       e?.message || 'Falha ao salvar usuário'
      setUsersError(errorMsg)
    }
  }

  const removeUser = async (id) => {
    const ok = window.confirm('Tem certeza que deseja excluir este usuário?')
    if (!ok) return
    try {
      await deleteUser(id)
      await refreshUsers()
    } catch (e) {
      setUsersError(e?.message || 'Falha ao excluir usuário')
    }
  }

  const openProfileEdit = (p) => {
    setSelectedProfile(p || null)
    setIsProfileModalOpen(true)
  }

  const closeProfileModal = () => {
    setSelectedProfile(null)
    setIsProfileModalOpen(false)
  }

  const saveProfile = async (data) => {
    try {
      if (selectedProfile && selectedProfile.id) {
        await updateProfile(selectedProfile.id, data)
      } else {
        await createProfile(data)
      }
      await refreshProfiles()
      closeProfileModal()
    } catch (e) {
      const errorMsg = e?.response?.data?.name?.[0] ||
                       e?.message || 'Falha ao salvar perfil'
      setProfilesError(errorMsg)
    }
  }

  const removeProfile = async (id) => {
    const ok = window.confirm('Tem certeza que deseja excluir este perfil?')
    if (!ok) return
    try {
      await deleteProfile(id)
      await refreshProfiles()
    } catch (e) {
      setProfilesError(e?.message || 'Falha ao excluir perfil')
    }
  }

  const handleResetPassword = async (user) => {
    const ok = window.confirm(`Tem certeza que deseja resetar a senha de ${user.username}? Uma senha temporária será gerada.`)
    if (!ok) return

    try {
      const response = await resetUserPassword(user.id)
      setResetPasswordDialog({
        open: true,
        user: user,
        tempPassword: response.temporary_password
      })
    } catch (e) {
      setUsersError(e?.message || 'Falha ao resetar senha')
    }
  }

  const closeResetPasswordDialog = () => {
    setResetPasswordDialog({ open: false, user: null, tempPassword: null })
  }

  return (
    <div className="space-y-6">
      {section === 'users' && (
        <>
          <Card title="Usuários">
            {usersError && <Typography color="error" variant="body2" sx={{ mb: 2 }}>{usersError}</Typography>}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} justifyContent="space-between" alignItems="center">
              <TextField
                placeholder="Buscar usuários..."
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                size="small"
                sx={{ minWidth: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openUserEdit(null)}
              >
                Novo Usuário
              </Button>
            </Stack>
            <Grid container spacing={2}>
              {usersLoading && Array.from({ length: 6 }).map((_, i) => (
                <Grid item xs={12} sm={6} lg={4} key={`u-skel-${i}`}>
                  <Card>
                    <Skeleton variant="circular" width={40} height={40} />
                    <Skeleton variant="text" />
                    <Skeleton variant="text" width="60%" />
                  </Card>
                </Grid>
              ))}
              {!usersLoading && (() => {
                const filtered = (users || []).filter(u => {
                  if (!userSearch) return true
                  const search = userSearch.toLowerCase()
                  return (
                    u.username?.toLowerCase().includes(search) ||
                    u.email?.toLowerCase().includes(search) ||
                    u.userprofile?.full_name?.toLowerCase().includes(search) ||
                    u.userprofile?.profile?.name?.toLowerCase().includes(search)
                  )
                })
                const startIndex = (userPage - 1) * itemsPerPage
                const paginatedUsers = filtered.slice(startIndex, startIndex + itemsPerPage)
                return paginatedUsers
              })().map((u, idx) => (
                <Grid item xs={12} sm={6} lg={4} key={u.id ?? idx}>
                  <Card>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Avatar>
                          <PersonIcon />
                        </Avatar>
                        <div>
                          <Typography variant="subtitle1">{u.userprofile?.full_name || u.username}</Typography>
                          <Typography variant="body2" color="text.secondary">{u.email || '—'}</Typography>
                        </div>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={u.is_active ? 'Ativo' : 'Inativo'} color={u.is_active ? 'success' : 'default'} size="small" />
                        <Tooltip title="Resetar Senha">
                          <IconButton size="small" onClick={() => handleResetPassword(u)} color="warning">
                            <LockResetIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openUserEdit(u)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" onClick={() => removeUser(u.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                    <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                      Perfil: {u.userprofile?.profile?.name || '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
                      Secretarias: {u.allow_all_secretarias
                        ? 'Todas'
                        : (u.secretarias && u.secretarias.length > 0
                            ? u.secretarias.map(s => s.secretaria).join(', ')
                            : 'Nenhuma')}
                    </Typography>
                  </Card>
                </Grid>
              ))}
              {!usersLoading && (!users || users.length === 0) && (
                <Grid item xs={12}><Typography variant="body2" color="text.secondary">Nenhum usuário encontrado.</Typography></Grid>
              )}
            </Grid>
            {!usersLoading && users && users.length > 0 && (() => {
              const filtered = (users || []).filter(u => {
                if (!userSearch) return true
                const search = userSearch.toLowerCase()
                return (
                  u.username?.toLowerCase().includes(search) ||
                  u.email?.toLowerCase().includes(search) ||
                  u.userprofile?.full_name?.toLowerCase().includes(search) ||
                  u.userprofile?.profile?.name?.toLowerCase().includes(search)
                )
              })
              const totalPages = Math.ceil(filtered.length / itemsPerPage)
              if (totalPages <= 1) return null
              return (
                <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={userPage}
                    onChange={(e, page) => setUserPage(page)}
                    color="primary"
                  />
                </Stack>
              )
            })()}
          </Card>

          <Card>
            <UserList />
          </Card>
        </>
      )}

      {section === 'profiles' && (
        <>
          <Card title="Perfis">
            {profilesError && <Typography color="error" variant="body2" sx={{ mb: 2 }}>{profilesError}</Typography>}
            <Stack direction="row" spacing={2} sx={{ mb: 2 }} justifyContent="space-between" alignItems="center">
              <TextField
                placeholder="Buscar perfis..."
                value={profileSearch}
                onChange={(e) => setProfileSearch(e.target.value)}
                size="small"
                sx={{ minWidth: 300 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openProfileEdit(null)}
              >
                Novo Perfil
              </Button>
            </Stack>
            <Grid container spacing={2}>
              {profilesLoading && Array.from({ length: 6 }).map((_, i) => (
                <Grid item xs={12} sm={6} lg={4} key={`p-skel-${i}`}>
                  <Card>
                    <Skeleton variant="text" />
                    <Skeleton variant="text" width="60%" />
                  </Card>
                </Grid>
              ))}
              {!profilesLoading && (() => {
                const filtered = (profiles || []).filter(p => {
                  if (!profileSearch) return true
                  const search = profileSearch.toLowerCase()
                  return (
                    p.name?.toLowerCase().includes(search) ||
                    p.description?.toLowerCase().includes(search)
                  )
                })
                const startIndex = (profilePage - 1) * itemsPerPage
                const paginatedProfiles = filtered.slice(startIndex, startIndex + itemsPerPage)
                return paginatedProfiles
              })().map((p, idx) => (
                <Grid item xs={12} sm={6} lg={4} key={p.id ?? idx}>
                  <Card>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                      <Avatar>
                        <BadgeIcon />
                      </Avatar>
                      <div>
                        <Typography variant="subtitle1">{p.name}</Typography>
                        <Typography variant="body2" color="text.secondary">{p.description || '—'}</Typography>
                      </div>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip title="Editar">
                          <IconButton size="small" onClick={() => openProfileEdit(p)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton size="small" onClick={() => removeProfile(p.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Stack>
                  </Card>
                </Grid>
              ))}
              {!profilesLoading && (!profiles || profiles.length === 0) && (
                <Grid item xs={12}><Typography variant="body2" color="text.secondary">Nenhum perfil encontrado.</Typography></Grid>
              )}
            </Grid>
            {!profilesLoading && profiles && profiles.length > 0 && (() => {
              const filtered = (profiles || []).filter(p => {
                if (!profileSearch) return true
                const search = profileSearch.toLowerCase()
                return (
                  p.name?.toLowerCase().includes(search) ||
                  p.description?.toLowerCase().includes(search)
                )
              })
              const totalPages = Math.ceil(filtered.length / itemsPerPage)
              if (totalPages <= 1) return null
              return (
                <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
                  <Pagination
                    count={totalPages}
                    page={profilePage}
                    onChange={(e, page) => setProfilePage(page)}
                    color="primary"
                  />
                </Stack>
              )
            })()}
          </Card>

        <Card>
          <ProfileList />
        </Card>
        </>
      )}
      <Modal isOpen={isUserModalOpen} title={selectedUser ? 'Editar Usuário' : 'Novo Usuário'} onClose={closeUserModal}>
        <UserForm user={selectedUser} onSave={saveUser} onCancel={closeUserModal} />
      </Modal>
      <Modal isOpen={isProfileModalOpen} title={selectedProfile ? 'Editar Perfil' : 'Novo Perfil'} onClose={closeProfileModal}>
        <ProfileForm profile={selectedProfile} onSave={saveProfile} onCancel={closeProfileModal} />
      </Modal>

      <Dialog open={resetPasswordDialog.open} onClose={closeResetPasswordDialog}>
        <DialogTitle>Senha Resetada</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A senha de <strong>{resetPasswordDialog.user?.username}</strong> foi resetada com sucesso.
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontWeight: 'bold', color: 'primary.main' }}>
            Senha temporária: {resetPasswordDialog.tempPassword}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, fontSize: '0.875rem' }}>
            Por favor, forneça esta senha ao usuário. Ele será obrigado a alterá-la no próximo login.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResetPasswordDialog} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}